// Uložení rozpracovaného košíku jako draft order.
//
// Vezme aktuální stav košíku z /cart.js, pošle ho na App Proxy endpoint
// aplikace bos-hk-draft-order a ta z něj založí draft order + nechá Shopify
// odeslat fakturu s výpisem položek a odkazem na dokončení.
//
// Konfiguraci (endpoint, přeložené hlášky) předává main-cart-footer.liquid
// přes window.saveCart — stejný vzor jako window.shippingCalculator.
class SaveCartForm extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.isSubmitting = false;
  }

  connectedCallback() {
    // ModalDialog.connectedCallback si modal přesune do <body>, čímž se tenhle
    // prvek odpojí a znovu připojí — connectedCallback tedy proběhne dvakrát.
    // Bez tohohle guardu by se listenery navěsily dvakrát a jedno kliknutí by
    // založilo dvě objednávky a poslalo dva e-maily.
    if (this.initialized) {
      return;
    }

    this.submitButton = this.querySelector("[data-save-cart-submit]");
    this.emailInput = this.querySelector('input[type="email"]');
    this.errorElement = this.querySelector("[data-save-cart-error]");
    this.successElement = this.querySelector("[data-save-cart-success]");
    this.spinner = this.querySelector(".button-overlay-spinner");
    this.config = window.saveCart || {};

    if (!this.submitButton || !this.emailInput) {
      return;
    }

    this.initialized = true;
    this.submitButton.addEventListener("click", this.onSubmit.bind(this));

    // Enter v inputu odešle formulář. Input není součástí <form>, aby omylem
    // neodeslal košík do checkoutu, takže si submit musíme obsloužit sami.
    this.emailInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        this.onSubmit();
      }
    });
  }

  async onSubmit() {
    if (this.isSubmitting) {
      return;
    }

    // Atribut multiple je nativní HTML validace, ale spec připouští jen čárky.
    // Outlook odděluje adresy středníkem a lidé je odtud kopírují, takže je
    // převedeme — a zapíšeme zpátky do pole, ať uživatel vidí, co odesílá.
    const email = this.emailInput.value.replace(/;/g, ",").trim();
    this.emailInput.value = email;

    if (!email || !this.emailInput.checkValidity()) {
      this.showError(this.config.errors?.email);
      this.emailInput.focus();
      return;
    }

    // Jen kvůli okamžité zpětné vazbě; rozhoduje MAX_RECIPIENTS na serveru.
    const recipientCount = this.countRecipients(email);
    if (this.config.maxRecipients && recipientCount > this.config.maxRecipients) {
      this.showError(this.config.errors?.tooMany);
      this.emailInput.focus();
      return;
    }

    this.setLoading(true);
    this.clearMessages();

    try {
      const cart = await this.fetchCart();

      if (!cart.items || cart.items.length === 0) {
        this.showError(this.config.errors?.empty);
        return;
      }

      // Posíláme jen variant ID a množství — ceny počítá Shopify na serveru.
      const response = await fetch(this.config.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: email,
          note: cart.note || "",
          attributes: cart.attributes || {},
          items: cart.items.map((item) => ({
            id: item.id,
            quantity: item.quantity,
            properties: item.properties || {},
          })),
        }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        this.showError(this.errorMessageFor(response.status, result?.error));
        return;
      }

      // Část příjemců mohla selhat, přestože nabídka vznikla a někomu dorazila.
      // Server to hlásí přes `failed`, ne chybovým stavem — kdyby vracel chybu,
      // zákazník by odesílal znovu a vznikaly by duplicitní nabídky.
      this.showSuccess(result.failed > 0);
    } catch (error) {
      console.error("[save-cart]", error);
      this.showError(this.config.errors?.generic);
    } finally {
      this.setLoading(false);
    }
  }

  async fetchCart() {
    const cartUrl = this.config.cartUrl || "/cart";
    const response = await fetch(`${cartUrl}.js`, {
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error(`/cart.js vrátil ${response.status}`);
    }

    return response.json();
  }

  countRecipients(value) {
    return value
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part.length > 0).length;
  }

  // Kód z těla odpovědi je přesnější než HTTP status — invalid_email
  // i too_many_recipients vracejí 400, takže podle statusu by obě daly
  // stejnou hlášku. Status zůstává jako záloha, když tělo chybí.
  errorMessageFor(status, code) {
    const byCode = {
      invalid_email: this.config.errors?.email,
      too_many_recipients: this.config.errors?.tooMany,
      empty_cart: this.config.errors?.empty,
      rate_limited: this.config.errors?.rateLimited,
    };

    if (code && byCode[code]) {
      return byCode[code];
    }

    if (status === 429) {
      return this.config.errors?.rateLimited;
    }
    if (status === 400) {
      return this.config.errors?.email;
    }
    return this.config.errors?.generic;
  }

  setLoading(isLoading) {
    this.isSubmitting = isLoading;
    this.submitButton.classList.toggle("loading", isLoading);
    this.submitButton.disabled = isLoading;
    this.spinner?.classList.toggle("hidden", !isLoading);
  }

  clearMessages() {
    this.errorElement?.classList.add("hidden");
    this.successElement?.classList.add("hidden");
  }

  showError(message) {
    if (!this.errorElement) {
      return;
    }

    this.errorElement.textContent = message || this.config.errors?.generic || "";
    this.errorElement.classList.remove("hidden");
  }

  showSuccess(hasFailures) {
    // Nabídka vznikla i při částečném selhání, takže formulář schováme vždy —
    // opakované odeslání by vyrobilo duplicitní draft order.
    this.hideForm();

    // Při částečném selhání se ukáže jen upozornění, ne obojí. Chybový prvek
    // je v DOM před úspěšným, takže by se hlášky vykreslily v obráceném
    // pořadí ("nepodařilo se" nad "hotovo"). Text upozornění proto sám říká,
    // že část adres obeslaná byla.
    if (hasFailures) {
      this.showError(this.config.errors?.partial);
      return;
    }

    if (!this.successElement) {
      return;
    }

    this.successElement.textContent = this.config.success || "";
    this.successElement.classList.remove("hidden");
  }

  // Po odeslání už není co měnit — schováme vstup i tlačítko.
  // Košík záměrně necháváme beze změny.
  hideForm() {
    this.emailInput.closest(".save-cart-field")?.classList.add("hidden");
    this.querySelector(".save-cart-hint")?.classList.add("hidden");
    this.submitButton.classList.add("hidden");
    this.querySelector(".save-cart-consent")?.classList.add("hidden");
    this.querySelector(".save-cart-text")?.classList.add("hidden");
  }
}

if (!customElements.get("save-cart-form")) {
  customElements.define("save-cart-form", SaveCartForm);
}
